import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  Stack,
  Radio,
  RadioGroup,
  TextField,
  MenuItem,
} from "@mui/material";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
  InfoOutlined,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { CustomTextBox } from "components/CustomComponents";
import { parentBoxStyle } from "services/constants";
import { useSelector } from "react-redux";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import dayjs from "dayjs";
import { useFetch } from "services/hooks/useFetch";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  FORM_ACTION_BUTTON_WIDTH,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
  withDesignFont,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  useExpandedContentLeft,
} from "components/DesignSystem/sharedUiPatterns";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

const REPAYMENT_MODE_OPTIONS = [
  {
    id: "Full",
    label: "Full Deduction",
    description: "Deduct the full amount from one payroll.",
  },
  {
    id: "Installment",
    label: "Installments",
    description: "Spread the deduction across multiple payrolls.",
  },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 52,
    borderRadius: "12px",
    backgroundColor: designColors.white,
    "& fieldset": {
      borderColor: designColors.slate200,
    },
    "&:hover fieldset": {
      borderColor: designColors.slate300,
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2563EB",
      borderWidth: 1.5,
    },
  },
  "& .MuiFormHelperText-root": {
    mt: 0.75,
    mx: 0.5,
  },
};

const optionGroupSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "stretch",
  gap: 2,
};

const optionCardSx = (active) => ({
  flex: "1 1 220px",
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`,
  borderRadius: "12px",
  p: 1.5,
  backgroundColor: active ? designColors.tealTint : designColors.white,
  transition: "border-color 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    borderColor: designColors.tealDark,
  },
});

const optionRadioSx = {
  color: designColors.slate300,
  "&.Mui-checked": {
    color: designColors.tealDark,
  },
};

// Repayment Frequency / Number of Installments / Repayment Amount sit
// side-by-side in a 3-column row. Their helper text lengths differ and
// "{Frequency} Repayment Amount" can wrap to two lines, so without a
// reserved label height each input starts at a different Y position
// depending on how many lines its own label happened to wrap to.
const INSTALLMENT_ROW_LABEL_MIN_HEIGHT = 96;

const infoCardSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.2,
  minHeight: 52,
  p: 1.5,
  borderRadius: "12px",
  border: `1px solid ${designColors.slate200}`,
  backgroundColor: "#F8FBFF",
};

const getDefaultFormValues = () => ({
  user_id: null,
  selectedUser: null,
  advance_type: "Salary",
  request_date: dayjs(),
  amount: "",
  reason: "",
  repayment_mode: "Full",
  repayment_date: dayjs().add(1, "month").startOf("month"),
  no_of_installments: 2,
  repayment_frequency: "Monthly",
  button_loader: false,
});

export const Apply = ({
  onClose,
  setReCallApi,
  open = true,
  expanded = false,
  onToggleExpand,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: getDefaultFormValues(),
  });

  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const { domain, userDetails, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );
  const contentLeft = useExpandedContentLeft(expanded);
  // Editor-only submissions must stay pending; approval requires approver access.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Advance Salary",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Advance Salary",
    );

  useEffect(() => {
    if (open) {
      reset(getDefaultFormValues());
      setShowAutoCompleteAvathar(false);
    }
  }, [open, reset]);

  const { result: membersData } = useFetch(
    `${domain}list/employee?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );

  const repaymentMode = watch("repayment_mode");
  const amount = watch("amount");
  const noOfInstallments = watch("no_of_installments");
  const repaymentFrequency = watch("repayment_frequency");
  const selectedUser = watch("selectedUser");
  const reason = watch("reason") || "";
  const isInstallment = repaymentMode === "Installment";

  const perPeriodAmount =
    isInstallment && amount && noOfInstallments > 0
      ? (parseFloat(amount) / parseInt(noOfInstallments, 10)).toFixed(2)
      : "0.00";

  const sanitizeAmount = (value) => {
    if (!value) return "";
    let sanitized = value.toString().replace(/\D/g, "");
    sanitized = sanitized.replace(/^0+/, "");
    sanitized = sanitized.slice(0, 8);
    return sanitized;
  };

  const updateRepaymentMode = (event) => {
    setValue("repayment_mode", event.target.value);
    setValue("no_of_installments", 2);
    setValue("repayment_frequency", "Monthly");
    setValue("repayment_date", dayjs().add(1, "month").startOf("month"));
  };

  const updateUserName = (event, data) => {
    if (data) {
      setValue("selectedUser", data);
      setValue("user_id", data.user_id || data.id);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("selectedUser", null);
      setValue("user_id", null);
      setShowAutoCompleteAvathar(true);
    }
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);

    const formattedRepaymentDate = data.repayment_date
      ? (isInstallment
          ? dayjs(data.repayment_date).startOf("month")
          : dayjs(data.repayment_date)
        ).format("YYYY-MM-DD")
      : "";

    const createPayload = {
      user_id: data.user_id,
      advance_type: "Salary",
      request_date: data.request_date
        ? dayjs(data.request_date).format("YYYY-MM-DD")
        : "",
      amount: parseFloat(data.amount),
      reason: data.reason,
      repayment_mode: data.repayment_mode,
      repayment_date: formattedRepaymentDate,
      no_of_installments: isInstallment ? parseInt(data.no_of_installments, 10) : 1,
      repayment_frequency: data.repayment_frequency,
    };

    if (!hasApproverAccess) {
      createPayload.status = "Pending";
    }

    const createRes = await customAPI(`${domain}advance/request/?company_id=${topbarCompanyId}`, "POST", createPayload);

    if (!createRes?.ok) {
      setValue("button_loader", false);
      showToast("error", createRes?.message || "Failed to create request.");
      return;
    }

    if (!hasApproverAccess) {
      showToast("success", "Advance request submitted successfully.");
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
      return;
    }

    const advanceId =
      createRes?.data?.id ||
      createRes?.responseDetails?.data?.id ||
      createRes?.responseDetails?.id;

    if (!advanceId) {
      setValue("button_loader", false);
      showToast("error", "Failed to retrieve advance ID for approval.");
      return;
    }

    const approvePayload = {
      approver_id: userDetails?.id,
      approved_amount: parseFloat(data.amount),
      repayment_mode: data.repayment_mode,
      approved_installments: isInstallment ? parseInt(data.no_of_installments, 10) : 1,
      approved_repayment_date: formattedRepaymentDate,
      approver_comments: "Auto-approved via Team Zone creation.",
      approved_repayment_frequency: data.repayment_frequency,
    };

    const approveRes = await customAPI(
      `${domain}advance/request/${advanceId}/approve/?company_id=${topbarCompanyId}`,
      "PUT",
      approvePayload,
    );

    if (approveRes?.ok) {
      showToast("success", "Advance request created and approved successfully.");
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast("error", approveRes?.message || "Failed to approve request.");
    }
  };

  const panelContent = (
    <Mui.ThemeProvider theme={withDesignFont}>
      <Box
        sx={{
          ...parentBoxStyle,
          height: "100%",
          borderRadius: expanded ? designRadii.card : 0,
          overflow: "hidden",
          backgroundColor: designColors.white,
        }}
      >
        <Paper elevation={0} sx={{ borderRadius: 0, backgroundColor: designColors.white }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Mui.Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{
                pb: 2.25,
                borderBottom: `1px solid ${designColors.slate200}`,
              }}
            >
              <Box>
                <Typography sx={designType.panelTitle}>Advance Request</Typography>
                <Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                  Submit a salary advance request for a team member.
                </Typography>
              </Box>
              <Mui.Stack direction="row" spacing={1} alignItems="center">
                {onToggleExpand ? (
                  expanded ? (
                    <PanelHeaderIconButton
                      expanded
                      title="Collapse"
                      onClick={onToggleExpand}
                    >
                      <CloseFullscreen />
                    </PanelHeaderIconButton>
                  ) : (
                    <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                      <IconButton
                        onClick={onToggleExpand}
                        size="small"
                        sx={{
                          ...designShell.panelSoftIconButton,
                          color: designColors.slate500,
                        }}
                      >
                        <OpenFullViewIcon fontSize="small" />
                      </IconButton>
                    </Mui.Tooltip>
                  )
                ) : null}
                {expanded ? (
                  <PanelHeaderIconButton
                    expanded
                    title="Close"
                    onClick={onClose}
                  >
                    <Close />
                  </PanelHeaderIconButton>
                ) : (
                  <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                      ...designShell.panelSoftIconButton,
                      color: designColors.slate500,
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Mui.Stack>
            </Mui.Stack>
          </Box>
        </Paper>

        <Mui.Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <Box>
                <GlobalFieldLabel helperText="Select the employee for whom the salary advance is being requested.">
                  Employee Name
                </GlobalFieldLabel>
                <CustomAutoCompleteWithLogo
                  disabled={!membersData || membersData.length === 0}
                  dataList={membersData
                    ?.filter((obj) => obj.user_name && obj.user_name.toLowerCase() !== "all")
                    ?.map((obj) => ({
                      ...obj,
                      label: obj.user_name,
                      image: obj.image_url,
                    }))}
                  selectedValue={watch("selectedUser")}
                  rules={{
                    ...register("user_id", { required: "Employee is required" }),
                  }}
                  updateDataFunction={updateUserName}
                  placeHolder={
                    !membersData || membersData.length === 0
                      ? "No employees available"
                      : "Select employee name"
                  }
                  errorMessage={errors?.user_id?.message || ""}
                  textBoxError={Boolean(errors.user_id)}
                  showAvathar={showAutoCompleteAvathar}
                  setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
                />
              </Box>

              {selectedUser ? (
                <RequestDetailHeader
                  userDetails={{
                    image_url: selectedUser?.image_url,
                    employee_id: selectedUser?.employee_id,
                    user_name: selectedUser?.label || selectedUser?.user_name,
                    department_name:
                      selectedUser?.department_name || selectedUser?.department,
                    designation: selectedUser?.designation,
                  }}
                />
              ) : null}

              <Box>
                <GlobalFieldLabel helperText="Select the date to be used for approval and payroll processing.">
                  Request Date
                </GlobalFieldLabel>
                <CustomizedDatePicker
                  name="request_date"
                  control={control}
                  required
                  requiredMessage="Request date is required"
                  placeholder="Select request date"
                />
              </Box>

              <Box>
                <GlobalFieldLabel helperText="Enter the amount of the advance you want to request.">
                  Amount
                </GlobalFieldLabel>
                <CustomTextBox
                  rules={{
                    ...register("amount", { required: "Amount is required" }),
                  }}
                  selectedValue={amount}
                  updateDataFunction={(event) =>
                    setValue("amount", sanitizeAmount(event.target.value))
                  }
                  placeHolder="Enter amount (₹)"
                  errorMessage={errors?.amount?.message || ""}
                  textBoxError={Boolean(errors.amount)}
                />
              </Box>

              <Box>
                <GlobalFieldLabel helperText="Enter the reason for requesting the advance.">
                  Reason
                </GlobalFieldLabel>
                <CustomTextBox
                  type="multiline"
                  rules={{
                    ...register("reason", { required: "Reason is required" }),
                  }}
                  selectedValue={reason}
                  updateDataFunction={(event) => {
                    if (event.target.value.length <= 250) {
                      setValue("reason", event.target.value);
                    }
                  }}
                  placeHolder="Enter reason for requesting the advance"
                  errorMessage={errors?.reason?.message || ""}
                  textBoxError={Boolean(errors.reason)}
                />
                <Typography sx={{ mt: 0.5, textAlign: "right", fontSize: 12.5, color: designColors.slate500 }}>
                  {reason.length} / 250
                </Typography>
              </Box>

              <Box>
                    <GlobalFieldLabel helperText="Choose how the advance will be deducted from the employee’s payroll.">
                      Repayment Option
                    </GlobalFieldLabel>
                    <RadioGroup
                      row
                      value={repaymentMode}
                      onChange={updateRepaymentMode}
                      sx={optionGroupSx}
                    >
                      {REPAYMENT_MODE_OPTIONS.map((opt) => {
                        const isActive = repaymentMode === opt.id;
                        return (
                          <Box key={opt.id} sx={optionCardSx(isActive)}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Radio value={opt.id} sx={optionRadioSx} />
                              <Typography
                                fontWeight="medium"
                                sx={{ color: isActive ? designColors.tealDark : designColors.slate800 }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color={designColors.slate600}
                              sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                            >
                              {opt.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>

                  {isInstallment ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <GlobalFieldLabel
                          helperText="Choose how often the installment will be deducted"
                          minHeight={INSTALLMENT_ROW_LABEL_MIN_HEIGHT}
                        >
                          Repayment Frequency
                        </GlobalFieldLabel>
                        <TextField
                          select
                          fullWidth
                          value={repaymentFrequency}
                          onChange={(event) => setValue("repayment_frequency", event.target.value)}
                          sx={inputSx}
                        >
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                          <MenuItem value="Semi-monthly">Semi-monthly</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <GlobalFieldLabel
                          helperText="Enter the number of payroll cycles to spread the repayment across"
                          minHeight={INSTALLMENT_ROW_LABEL_MIN_HEIGHT}
                        >
                          Number of Installments
                        </GlobalFieldLabel>
                        <TextField
                          fullWidth
                          type="number"
                          value={noOfInstallments}
                          onChange={(event) => {
                            const value = parseInt(event.target.value, 10);
                            setValue(
                              "no_of_installments",
                              Number.isNaN(value) ? "" : Math.max(2, value),
                            );
                          }}
                          inputProps={{ min: 2 }}
                          sx={inputSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <GlobalFieldLabel
                          helperText="Automatically calculated from the amount and number of installments"
                          minHeight={INSTALLMENT_ROW_LABEL_MIN_HEIGHT}
                        >
                          {repaymentFrequency} Repayment Amount
                        </GlobalFieldLabel>
                        <Box
                          sx={{
                            minHeight: 52,
                            px: 1.6,
                            borderRadius: "12px",
                            border: `1px solid ${designColors.slate200}`,
                            backgroundColor: "#F8FBFF",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: "#2563EB" }}>
                            ₹ {Number(perPeriodAmount).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : null}

                  <Box>
                    <GlobalFieldLabel
                      helperText={
                        isInstallment
                          ? "Select the month the first installment will be deducted"
                          : "Select the payroll cycle in which the advance will be deducted."
                      }
                    >
                      {isInstallment ? "Repayment Start" : "Repayment Date"}
                    </GlobalFieldLabel>
                    <CustomizedDatePicker
                      key={isInstallment ? "repayment-month" : "repayment-date"}
                      name="repayment_date"
                      control={control}
                      required
                      requiredMessage={
                        isInstallment
                          ? "Repayment start is required"
                          : "Repayment date is required"
                      }
                      placeholder={
                        isInstallment ? "Select repayment start month" : "Select repayment date"
                      }
                      format={isInstallment ? "MM/YYYY" : "DD/MM/YYYY"}
                      views={isInstallment ? ["year", "month"] : ["year", "month", "day"]}
                      minDate={
                        isInstallment
                          ? dayjs().add(1, "month").startOf("month").format("YYYY-MM-DD")
                          : dayjs().format("YYYY-MM-DD")
                      }
                    />
                  </Box>

                  <Box sx={infoCardSx}>
                    <InfoOutlined sx={{ fontSize: 18, color: "#2563EB", mt: 0.2 }} />
                    <Typography sx={{ fontSize: 13.5, color: designColors.slate600 }}>
                      The advance amount will be recovered through payroll as per the selected option.
                    </Typography>
                  </Box>
            </Stack>
          </Box>

          <Mui.Box
            sx={{
              px: 3,
              py: 1.25,
              borderTop: `1px solid ${designColors.slate200}`,
              backgroundColor: designColors.white,
            }}
          >
            <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
              <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
              <PrimaryButton
                type="submit"
                onClick={handleSubmit(onSubmit)}
                loading={Boolean(watch("button_loader"))}
                disabled={Boolean(watch("button_loader"))}
                sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
              >
                {hasApproverAccess ? "Approve" : "Apply"}
              </PrimaryButton>
            </Mui.Stack>
          </Mui.Box>
        </Mui.Box>
      </Box>
    </Mui.ThemeProvider>
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx()}
    >
      <Box sx={{ width: PANEL_DRAWER_WIDTH, maxWidth: "100%", height: "100%" }}>
        {panelContent}
      </Box>
    </Mui.Drawer>
  );
};
